import { useEffect, useState, useCallback, useRef } from 'react'
import { Layout } from '../components/Layout'
import { DeviceTable } from '../components/DeviceTable'
import { StatusBadge } from '../components/StatusBadge'
import { useRealtimeDevices } from '../services/websocket'
import { deviceService, networkService } from '../services'
import type { Network, Device } from '../types'
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Globe,
  Network as NetworkIcon,
  RefreshCw,
  Wifi,
  Zap,
} from 'lucide-react'

const SETTINGS_KEY = 'zt_dashboard_settings'
const DEFAULT_POLL_INTERVAL_MS = 10_000

function getStoredPollInterval(): number {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const ms = Number(parsed.pollInterval) * 1000
      if (Number.isFinite(ms) && ms > 0) return ms
    }
  } catch {
    // ignore malformed settings
  }
  return DEFAULT_POLL_INTERVAL_MS
}

export function DashboardPage() {
  const [network, setNetwork] = useState<Network | null>(null)
  const [networkLoading, setNetworkLoading] = useState(true)
  const [networkError, setNetworkError] = useState('')

  const [devicesLoading, setDevicesLoading] = useState(true)
  const [devicesError, setDevicesError] = useState('')
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const [initialDevices, setInitialDevices] = useState<Device[]>([])
  const { devices, setDevices, wsEnabled, setWsEnabled, wsConnected } = useRealtimeDevices(initialDevices)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchDevices = useCallback(async () => {
    try {
      setDevicesError('')
      setDevicesLoading(true)
      const data = await deviceService.listDevices()
      setInitialDevices(data.items)
      setDevices(data.items)
      setLastUpdated(new Date())
    } catch (err) {
      setDevicesError(err instanceof Error ? err.message : 'Failed to fetch devices')
    } finally {
      setDevicesLoading(false)
    }
  }, [setDevices])

  const fetchNetwork = useCallback(async () => {
    try {
      setNetworkError('')
      setNetworkLoading(true)
      const data = await networkService.getNetwork()
      setNetwork(data)
    } catch (err) {
      setNetworkError(err instanceof Error ? err.message : 'Failed to fetch network')
    } finally {
      setNetworkLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchNetwork()
    fetchDevices()

    pollIntervalRef.current = setInterval(() => {
      fetchDevices()
    }, getStoredPollInterval())

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
    }
  }, [fetchDevices, fetchNetwork])

  const onlineCount = devices.filter(d => d.online).length
  const offlineCount = devices.length - onlineCount

  const stats = [
    {
      label: 'Total Devices',
      value: devices.length,
      icon: NetworkIcon,
      color: 'text-primary',
      bg: 'bg-primary-50',
    },
    {
      label: 'Online',
      value: onlineCount,
      icon: Wifi,
      color: 'text-success',
      bg: 'bg-success/10',
    },
    {
      label: 'Offline',
      value: offlineCount,
      icon: AlertCircle,
      color: 'text-danger',
      bg: 'bg-danger/10',
    },
    {
      label: 'Avg Latency',
      value: devices.length
        ? `${Math.round(
            devices.reduce((sum, d) => sum + (d.latency || 0), 0) / devices.filter(d => d.latency).length || 1
          )}ms`
        : '—',
      icon: Zap,
      color: 'text-warning',
      bg: 'bg-warning/10',
    },
  ]

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Dashboard</h1>
            <p className="text-text-muted text-sm">
              Monitor your ZeroTier network and connected devices
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setWsEnabled(v => !v)}
              className={`btn ${wsEnabled ? 'btn-success' : 'btn-secondary'} text-sm inline-flex items-center gap-2`}
              title={wsEnabled ? 'WebSocket live updates enabled' : 'WebSocket live updates disabled'}
            >
              <Activity className={`w-4 h-4 ${wsConnected ? 'animate-pulse' : ''}`} />
              {wsEnabled ? (wsConnected ? 'Live' : 'Connecting...') : 'Live Off'}
            </button>

            <button
              type="button"
              onClick={fetchDevices}
              disabled={devicesLoading}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${devicesLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map(stat => (
            <div key={stat.label} className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-text-muted text-sm">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.bg}`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Network Overview</h2>
              <p className="text-sm text-text-muted">Pluto-WiFi configuration and status</p>
            </div>
          </div>

          {networkLoading && !network ? (
            <div className="skeleton h-20 rounded-md" />
          ) : networkError ? (
            <div className="p-4 rounded-md bg-danger/10 text-danger text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {networkError}
            </div>
          ) : network ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="p-3 rounded-lg bg-surface-elevated">
                <p className="text-text-muted">Name</p>
                <p className="font-medium">{network.name}</p>
              </div>
              <div className="p-3 rounded-lg bg-surface-elevated">
                <p className="text-text-muted">Network ID</p>
                <p className="font-mono font-medium">{network.id}</p>
              </div>
              <div className="p-3 rounded-lg bg-surface-elevated">
                <p className="text-text-muted">Subnet</p>
                <p className="font-mono font-medium">{network.subnet}</p>
              </div>
              <div className="p-3 rounded-lg bg-surface-elevated">
                <p className="text-text-muted">Status</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <StatusBadge online={network.enabled} />
                  <span className="text-text-muted">{network.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Devices</h2>
                <p className="text-sm text-text-muted">All devices on the network</p>
              </div>
            </div>

            {lastUpdated && (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Clock className="w-4 h-4" />
                Updated {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>

          {devicesError && (
            <div className="mb-4 p-4 rounded-md bg-danger/10 text-danger text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {devicesError}
            </div>
          )}

          <DeviceTable devices={devices} isLoading={devicesLoading} />
        </div>
      </div>
    </Layout>
  )
}
